<template>
  <SideSheet
    class="risk-signature"
    data-testid="risk-signature"
    position="right"
    :is-open="isOpen"
    @close="$emit('close')"
  >
    <NavBar sticky>
      <template #left>
        <BackButton icon="EF0030" @click="$emit('close')" />
      </template>
    </NavBar>
    <section class="risk-signature__content">
      <HeadingDisplay
        size="small"
        class="risk-signature__header"
        :title="headingData.title"
        :subtitle="headingData.subtitle"
      />
      <div class="risk-signature__risk-box">
        <Heading :level="6">Fora do nível de risco</Heading>
        <div class="risk-signature__risks">
          <div
            v-for="(risk, index) in risks"
            :key="index"
            class="risk-signature__risks--item"
          >
            <BaseIcon v-if="risk.isSelected" icon="EA0210" />
            <div :class="`risk-signature__risks--${risk.class}`" />
          </div>
        </div>
        <ListDisplay
          v-for="(item, index) in details"
          :key="index"
          :title="item.label"
          :value="item.value"
        />
      </div>
    </section>
    <ButtonContainer class="risk-signature__button-container">
      <template #button-primary>
        <ButtonPrimary :label="buttonLabel" size="large" @click="handleClick" />
      </template>
    </ButtonContainer>
  </SideSheet>
</template>

<script lang="ts" setup>
import { computed, watch } from 'vue';

import {
  NavBar,
  HeadingDisplay,
  Heading,
  BaseIcon,
  ListDisplay,
  ButtonContainer,
  ButtonPrimary
} from '@warrenbrasil/nebraska-web';
import { SideSheet } from '@/modules/common/components/SideSheet';
import { BackButton } from '@/modules/common/components/buttons';

import { removeBodyScroll } from '@/modules/common/helpers/browser';
import { isImpersonate } from '@/modules/common/services/partner';
import { useRouter } from 'vue-router/composables';

enum ERiskGrade {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH'
}

interface IData {
  customerRiskProfile: {
    label: string;
    value: string;
  }[];
  requestRisksSignature: {
    formatted: {
      formatted: string;
      value: string;
    };
    label: string;
    productId: string;
  }[];
}

const router = useRouter();

interface EmitEvents {
  (event: 'close'): void;
  (event: 'confirm'): void;
}
const emits = defineEmits<EmitEvents>();

interface Props {
  isOpen: boolean;
  data: IData;
}
const props = defineProps<Props>();

const headingData = computed(() => {
  return isImpersonate()
    ? {
        title:
          'Não é possivel sugerir produtos fora do nível de risco do cliente',
        subtitle:
          'Sugira produtos para o cliente dentro do nível de risco indicado para o perfil de investimento do cliente.'
      }
    : {
        title: 'Este produto não é indicado para o seu perfil de investimento',
        subtitle:
          'Ao continuar, você declara que está ciente e quer investir em um produto fora do nível de risco indicado para o seu perfil.'
      };
});

const buttonLabel = computed(() => {
  return isImpersonate() ? 'Voltar para o catálogo' : 'Continuar';
});

const risks = computed(() => {
  return [
    {
      class: 'low',
      isSelected:
        props.data.requestRisksSignature[0].formatted.value === ERiskGrade.LOW
    },
    {
      class: 'medium',
      isSelected:
        props.data.requestRisksSignature[0].formatted.value ===
        ERiskGrade.MEDIUM
    },
    {
      class: 'high',
      isSelected:
        props.data.requestRisksSignature[0].formatted.value === ERiskGrade.HIGH
    }
  ];
});

const details = computed(() => {
  const risk = {
    label: props.data.requestRisksSignature[0].label,
    value: props.data.requestRisksSignature[0].formatted.formatted
  };

  const list = props.data.customerRiskProfile;
  list.push(risk);

  return list;
});

function handleClick() {
  if (isImpersonate()) {
    router.push({
      name: 'productsCatalog',
      params: {
        type: 'fixed-income'
      }
    });

    return;
  }

  emits('confirm');
}

watch(
  () => props.isOpen,
  _isOpen => {
    removeBodyScroll();
  }
);
</script>

<style lang="less" scoped>
.risk-signature {
  height: 100%;

  :deep(.panel-content.right) {
    max-width: 683px;
    width: 100%;
  }

  .navbar-container {
    border-bottom: none;
  }

  &__content {
    max-width: 571px;
    width: 100%;
    margin: 0 auto;
    padding: @size-spacing-x800 @size-spacing-x500 @size-spacing-x1400;
  }

  &__header {
    margin-bottom: @size-spacing-x800;
  }

  &__risk-box {
    background-color: @background-tertiary;
    padding: @size-spacing-x700 @size-spacing-x800;
    border-radius: @size-radius-x300;
  }

  &__risks {
    width: 100%;
    display: flex;
    align-items: flex-end;
    margin-bottom: @size-spacing-x600;

    &--item {
      width: 33%;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    &--low {
      height: 32px;
      width: 100%;
      background-color: @risk-low;
      border-radius: @size-radius-x300 0 0 @size-radius-x300;
    }

    &--medium {
      height: 64px;
      width: 100%;
      background-color: @risk-medium;
      border-radius: @size-radius-x300 0 0 0;
    }

    &--high {
      height: 104px;
      width: 100%;
      background-color: @risk-high;
      border-radius: @size-radius-x300 @size-radius-x300 @size-radius-x300 0;
    }
  }

  .list-display {
    margin-bottom: @size-spacing-x350;

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  &__button-container {
    position: fixed;
    bottom: 0;
    right: 0;
    max-width: 683px;
    width: 100%;
    background: @background-secondary;
    z-index: 10;
  }
}
</style>
